import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import {
  LogoutOutlined,
  EditOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import api from '@api/api';
import { ReactComponent as UserSvg } from '@assets/svg/user.svg';
import CustomModal from '@components/CustomModal/CustomModal';
import LanguageSelect from '@components/LanguageSelect/LanguageSelect';
import { regexStandardRules } from '@fe-share-utils/regexRules';
import useCustomConfirmModal from '@hooks/useCustomConfirmModal';
import useCustomType1Modal from '@hooks/useCustomType1Modal';
import { CommonResponseObj } from '@models/common';
import { IRootState } from '@store/reducers';
import { logOut } from '@store/reducers/userSlice';
import { App, Button, Dropdown, Avatar, Form, Input } from 'antd';
import md5 from 'md5';

import type { AppDispatch } from '@store/index';
import type { MenuProps } from 'antd';

import './Header.scss';

async function handleApi(
  url: string,
  payload: object,
): Promise<{ code: number; msg: string }> {
  const res = await api.post(url, payload);
  const { code, msg }: CommonResponseObj = res.data;
  return { code, msg };
}

function Header() {
  const { message } = App.useApp();
  const { t } = useTranslation();

  const [confirmLogout] = useCustomConfirmModal();
  const [changePasswordSuccessModal] = useCustomType1Modal();
  const [isUpdatePasswordModalOpen, setIsUpdatePasswordModalOpen] =
    useState(false);
  const [isUpdatePreferencesModalOpen, setIsUpdatePreferencesModalOpen] =
    useState(false);

  const account = useSelector((state: IRootState) => state.userSlice.account);
  const user = useSelector((state: IRootState) => state.userSlice);
  const [updatePasswordForm] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();

  async function handleLogout() {
    await dispatch(logOut());
  }

  const userDropDownItemList: MenuProps['items'] = [
    {
      key: 'btn-updatePassword',
      icon: <EditOutlined />,
      onClick: () => {
        setIsUpdatePasswordModalOpen(true);
      },
      label: t('Common.ChangePassword'),
    },
    ...(localStorage.getItem('engMode') === 'true'
      ? [
          {
            key: 'btn-updatePreferences',
            icon: <SettingOutlined />,
            onClick: () => {
              setIsUpdatePreferencesModalOpen(true);
            },
            label: t('Header.Preferences'),
          },
        ]
      : []),
    {
      key: 'btn-logout',
      icon: <LogoutOutlined />,
      onClick: () => {
        confirmLogout({
          title: t('Header.ConfirmLogout'),
          cancelText: t('Common.Cancel'),
          onOk: handleLogout,
        });
      },
      label: t('Header.Logout'),
    },
  ];

  function handleCancelUpdatePassword() {
    updatePasswordForm.resetFields();
    setIsUpdatePasswordModalOpen(false);
  }

  async function handleSubmitUpdatePassword() {
    try {
      const values = await updatePasswordForm.validateFields();

      const { oldPassword, newPassword, repeatNewPassword } = values;

      if (newPassword !== repeatNewPassword) {
        // TODO: 待 spec 定義錯誤文本
        message.error(t('Header.NewPasswordMismatch'));
        return;
      }
      if (newPassword === oldPassword) {
        // TODO: 待 spec 定義錯誤文本
        message.error(t('Header.NewPasswordSameAsOld'));
        return;
      }

      const hashedOldPassword = md5(oldPassword);
      const hashedNewPassword = md5(newPassword);

      const userId = Number(localStorage.getItem('userId'));
      const sellerId = Number(localStorage.getItem('sellerId'));

      // validate old password
      const oldPwdValidation = await handleApi(
        '/api/config-service/user/checkPwd',
        { password: hashedOldPassword, id: userId, sellerId, account },
      );
      if (oldPwdValidation.code !== 0) {
        message.error(oldPwdValidation.msg);
        return;
      }

      // update new password
      const newPwdUpdateResult = await handleApi(
        '/api/config-service/user/updatePwd',
        { password: hashedNewPassword, id: userId, sellerId, account },
      );
      if (newPwdUpdateResult.code !== 0) {
        message.error(newPwdUpdateResult.msg);
        return;
      }
      setIsUpdatePasswordModalOpen(false);
      changePasswordSuccessModal({
        title: t('Header.PasswordChangedSuccess'),
      });
      updatePasswordForm.resetFields();
    } catch (err) {
      console.warn('密碼不符規則', err);
    }
  }

  return (
    <div className="Header">
      <div className="Header__container">
        <div className="Header__user">
          <Dropdown
            overlayClassName="Header__user-dropDown"
            menu={{ items: userDropDownItemList }}
            placement="bottomLeft"
          >
            <Button type="text" className="Header__user-dropDownTrigger">
              <Avatar src={<UserSvg className="Header__user-svg" />} />
              <span className="Header__user-name">{user.name}</span>
            </Button>
          </Dropdown>
        </div>
      </div>
      <CustomModal
        title={t('Common.ChangePassword')}
        isOpen={isUpdatePasswordModalOpen}
        width={500}
        onCancel={() => handleCancelUpdatePassword()}
        onConfirm={() => handleSubmitUpdatePassword()}
      >
        <Form
          layout="vertical"
          form={updatePasswordForm}
          initialValues={{ account }}
        >
          <Form.Item name="account" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="oldPassword"
            label={t('Header.OldPassword')}
            rules={[regexStandardRules.PASSWORD(t)]}
          >
            <Input.Password
              placeholder={t('Header.EnterOldPassword')}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label={t('Common.NewPassword')}
            rules={[regexStandardRules.PASSWORD(t)]}
          >
            <Input.Password
              placeholder={t('Common.EnterNewPassword')}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            name="repeatNewPassword"
            label={t('Common.ConfirmNewPassword')}
            rules={[regexStandardRules.PASSWORD(t)]}
          >
            <Input.Password
              placeholder={t('Common.ReEnterNewPassword')}
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </CustomModal>
      <CustomModal
        title={t('Header.Preferences')}
        isOpen={isUpdatePreferencesModalOpen}
        width={500}
        onCancel={() => setIsUpdatePreferencesModalOpen(false)}
        onConfirm={() => setIsUpdatePreferencesModalOpen(false)}
      >
        <LanguageSelect />
      </CustomModal>
    </div>
  );
}

export default Header;
